/**
 * Drop-target de bloques sobre la preview (editor in-app).
 *
 * El autor arrastra un bloque de la paleta del panel (iframe del dashboard) y
 * lo suelta SOBRE la guía pintada en la app. El drag & drop HTML5 cruza
 * iframes de forma nativa, así que acá solo escuchamos `dragover`/`drop` en el
 * host de la guía, mostramos una línea de inserción entre bloques y avisamos
 * dónde cayó (el panel inserta el bloque en su estado y re-previsualiza).
 *
 * Contrato con el panel: el MIME `application/x-veo-block`. El `dragstart` de
 * la paleta lo setea con el tipo de bloque; durante `dragover` solo los TYPES
 * del dataTransfer son visibles (el valor recién se puede leer en el `drop`) —
 * alcanza para filtrar drags ajenos.
 *
 * La posición se expresa como `beforeIndex`: índice del bloque del paso ANTES
 * del cual insertar (`null` = al final). Los renderers estampan
 * `data-veo-idx` en cada elemento top-level con el índice ORIGINAL dentro de
 * `step.blocks[]` (una fila de botones agrupados lleva el del primero), así el
 * índice sobrevive a bloques filtrados u ocultos (p.ej. botones en pasos de
 * tour).
 */

export const VEO_BLOCK_MIME = 'application/x-veo-block';

/** Dónde soltó el autor un bloque nuevo. */
export interface BlockDropPayload {
  stepIndex: number;
  /** Tipo de bloque arrastrado (title/text/button/image — lo valida el panel). */
  blockType: string;
  /** Índice del bloque ANTES del cual insertar; `null` = al final. */
  beforeIndex: number | null;
}

export interface BlockDropTargetHandle {
  /**
   * Drag por POINTER (el camino principal): el panel manda las coordenadas del
   * puntero ya convertidas a la página de la app. Muestra/mueve la línea de
   * inserción; devuelve `true` si el punto está sobre la guía.
   */
  hoverAt(x: number, y: number): boolean;
  /**
   * Suelta en esas coordenadas: devuelve el punto de inserción, o `null` si el
   * puntero no estaba sobre la guía (el drop se descarta). Siempre borra la línea.
   */
  dropAt(x: number, y: number): { beforeIndex: number | null } | null;
  /** Cancela el hover (borra la línea sin soltar). */
  cancelHover(): void;
  detach(): void;
}

interface AttachOptions {
  host: HTMLElement;
  stepIndex: number;
  onDrop: (payload: BlockDropPayload) => void;
}

/** Punto de inserción calculado bajo el cursor. */
interface DropSpot {
  beforeIndex: number | null;
  /** Coordenada Y (viewport) de la línea indicadora. */
  y: number;
  rect: DOMRect;
}

/**
 * Adjunta el drop-target al host de la guía en preview. Devuelve un handle
 * para desmontarlo (cambio de preview / cierre del editor).
 */
export function attachBlockDropTarget(options: AttachOptions): BlockDropTargetHandle {
  const { host, stepIndex, onDrop } = options;
  const doc = host.ownerDocument;
  let line: HTMLElement | null = null;
  let spot: DropSpot | null = null;

  const contentEl = (): HTMLElement | null =>
    host.shadowRoot?.querySelector<HTMLElement>('.veo-guide-content') ?? null;

  /** Hijos top-level que representan bloques (estampados por los renderers). */
  const blockChildren = (container: HTMLElement): HTMLElement[] =>
    Array.from(container.children).filter(
      (el): el is HTMLElement => el instanceof HTMLElement && el.dataset.veoIdx !== undefined,
    );

  const hideLine = (): void => {
    line?.remove();
    line = null;
    spot = null;
  };

  const showLine = (at: DropSpot): void => {
    if (!line) {
      line = doc.createElement('div');
      // Inline (no hay hoja de estilos del builder en el shadow de la guía).
      line.style.cssText =
        'position:fixed;height:3px;border-radius:2px;background:#FF5B35;' +
        'box-shadow:0 0 0 1px rgba(255,255,255,0.65);pointer-events:none;z-index:2147483647;';
      host.shadowRoot?.appendChild(line);
    }
    line.style.left = `${at.rect.left + 4}px`;
    line.style.width = `${Math.max(0, at.rect.width - 8)}px`;
    line.style.top = `${at.y - 1.5}px`;
  };

  const isBlockDrag = (event: DragEvent): boolean => {
    const types = event.dataTransfer?.types;
    return !!types && Array.from(types).includes(VEO_BLOCK_MIME);
  };

  /** Margen alrededor del contenido dentro del cual el drop sigue activo. */
  const NEAR_PX = 48;

  /** Boundary de inserción más cercano al cursor (por el eje Y). */
  const locate = (clientY: number): DropSpot | null => {
    const container = contentEl();
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    const kids = blockChildren(container);
    let beforeIndex: number | null = null;
    let y = kids.length === 0 ? rect.top + rect.height / 2 : 0;
    for (const kid of kids) {
      const r = kid.getBoundingClientRect();
      if (clientY < r.top + r.height / 2) {
        const idx = Number(kid.dataset.veoIdx);
        beforeIndex = Number.isFinite(idx) ? idx : null;
        y = r.top;
        break;
      }
      y = r.bottom; // el cursor quedó debajo: candidato "después del último"
    }
    return { beforeIndex, y, rect };
  };

  /** ¿El punto está sobre (o cerca de) el contenido de la guía? */
  const overContent = (x: number, y: number, rect: DOMRect): boolean =>
    x >= rect.left - NEAR_PX &&
    x <= rect.right + NEAR_PX &&
    y >= rect.top - NEAR_PX &&
    y <= rect.bottom + NEAR_PX;

  const hoverAt = (x: number, y: number): boolean => {
    const at = locate(y);
    if (!at || !overContent(x, y, at.rect)) {
      hideLine();
      return false;
    }
    spot = at;
    showLine(at);
    return true;
  };

  const dropAt = (x: number, y: number): { beforeIndex: number | null } | null => {
    const at = locate(y);
    hideLine();
    if (!at || !overContent(x, y, at.rect)) return null;
    return { beforeIndex: at.beforeIndex };
  };

  const onDragOver = (event: DragEvent): void => {
    if (!isBlockDrag(event)) return;
    const at = locate(event.clientY);
    if (!at) return;
    event.preventDefault(); // habilita el drop
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    spot = at;
    showLine(at);
  };

  const onDropEvent = (event: DragEvent): void => {
    if (!isBlockDrag(event)) return;
    event.preventDefault();
    const blockType = event.dataTransfer?.getData(VEO_BLOCK_MIME) ?? '';
    const at = spot ?? locate(event.clientY);
    hideLine();
    if (!blockType || !at) return;
    onDrop({ stepIndex, blockType, beforeIndex: at.beforeIndex });
  };

  const onDragLeave = (event: DragEvent): void => {
    // Solo al salir del host de verdad (los cruces internos del shadow se
    // re-targetean al host y no llegan acá).
    const to = event.relatedTarget;
    if (to instanceof Node && (to === host || host.contains(to))) return;
    hideLine();
  };

  host.addEventListener('dragover', onDragOver);
  host.addEventListener('drop', onDropEvent);
  host.addEventListener('dragleave', onDragLeave);

  return {
    hoverAt,
    dropAt,
    cancelHover: hideLine,
    detach(): void {
      host.removeEventListener('dragover', onDragOver);
      host.removeEventListener('drop', onDropEvent);
      host.removeEventListener('dragleave', onDragLeave);
      hideLine();
    },
  };
}
